/*
 * Date: 2025
 * Description: Top bar menu for extra interactions
 * Author: Philippe Leroux @ skitsc
 */

//Modules
import { useContext, useState , ReactElement , useRef, useEffect , cloneElement } from "react";
import { Box, IconButton, Tooltip , Menu, MenuItem, Badge, Collapse, Typography,  CircularProgress } from "@mui/material";
import { Link , useLocation, useNavigate  } from "react-router-dom";

//Icons
import SettingsIcon from '@mui/icons-material/Settings';
import HomeIcon from '@mui/icons-material/Home';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import ViewListIcon from '@mui/icons-material/ViewList';
import LogoutIcon from '@mui/icons-material/Logout';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import LibraryBooksIcon from '@mui/icons-material/LibraryBooks';
import { Message } from "@mui/icons-material";
import ShoppingBasketIcon from '@mui/icons-material/ShoppingBasket';

//Context
import { MainContext, ThemeContext } from "../../context/context";
import { useSocketEvent } from "../../context/socket.context";

//Interfaces
import { i_list, i_parts } from "../../interfaces/items.interface";
import { i_main_props } from "../../interfaces/utility.interface";
import { i_socket_response } from "../../interfaces/utility.interface";

//Contants && functions
import { default_list } from "../../utils/constant";
import { handleFetch } from "../../api/fetch";

//Components
import CartRow from "./cart.row";
import Footer from "../utils/footer";

//Utilities
import { delay, renderTotal } from "../../utils/utility";


const TopBar = (props : i_main_props) : ReactElement => {
    const { mode } = useContext(ThemeContext)

    const { HandleLogout , user } = useContext(MainContext)
    const nav = useNavigate();
    const [ cartOpen , setCartOpen ] = useState<boolean>(false)
    const [ refresh , setRefresh ] = useState<number>(1)
    const [ list , setList ] = useState<i_list>(default_list)
    const [ loading , setLoading ] = useState<boolean>(false)
    const [ error , setError ] = useState<string>('')
    const [ settingsAnchorEl, setSettingsAnchorEl]  = useState<null | HTMLElement>(null);
    const settingsMenuOpen = Boolean(settingsAnchorEl);
    const cartFetchedRef = useRef(false);
    const handleSettingsToggle = (event: React.MouseEvent<HTMLElement>) => {
        setSettingsAnchorEl(settingsAnchorEl ? null : event.currentTarget);
    };
    const location = useLocation()
    const handleClose = () => {
        setSettingsAnchorEl(null);
    };
    const [ channel ] = useState<string>('list')
    useSocketEvent(channel , (output : i_socket_response ) => {
        if(output.type === 'Update'){
           if(output.item.status && output.item.status === 'pending'){
               setRefresh(refresh + 1)
           }
        }
        if(output.type === 'Add'){
            const find = list.items.find(( item : i_parts ) => item._id === output.item._id)
            if(!find) setList({...list, items: [...list.items, output.item]})
        }
    })
    useEffect(() => {
        const getCart = async() => {
            setLoading(true)
            const res = await handleFetch('/list' , 'GET' , true , null)
            if(res.type === 'Success'){
                setList(res.data)
            }
            if(res.type === 'Unauthorized'){
                HandleLogout(nav)
            }
            setLoading(false)
        }
        getCart()
    },[refresh])
    useEffect(() => {
        if(cartOpen){
            const getCart = async() => {
                setLoading(true)
                const res = await handleFetch('/list' , 'GET' , true , null)
                if(res.type === 'Success'){
                    setList(res.data)
                }
                if(res.type === 'Unauthorized'){
                    HandleLogout(nav)
                }
                setLoading(false)
            }
            getCart()
        }
    },[cartOpen])
    const handleCart = () => {
        if (!cartFetchedRef.current) {
            cartFetchedRef.current = true;
        }
        setCartOpen(true);
    }
    const closeCart = () => {
        if(cartOpen)
        setCartOpen(false)
    }
    const sizing = {
        width : '45px',
        height : '45px'
    }
    const pathing = props.uploadPath + props.path + '/uploads/parts/'
    const handleDelete = async( part : i_parts ) => {
        const method = 'PATCH'
        const arg : string = "/"+ part._id  + '/' + 'Delete'
        const res = await handleFetch('/list' + arg , method , true , null)
        if(res.type === 'Success'){
            const updatedItems = list.items.filter(item => item._id !== part._id);
            const currentList = { ...list, items: updatedItems };
            setList(currentList)
        }else{
            if(res.type === 'Unauthorized'){
                HandleLogout(nav)
                return
            }else{
                setError(res.message)
                delay(3000).then(() => setError(''))
            }
        }
    }
    const landingStyle = { backgroundImage : "url('/img/dash.webp')" , backgroundSize : 'cover' , backgroundPosition : 'center' }
    const catalogStyle = { backgroundImage : "url('/img/bg_2.webp')" , backgroundSize : 'cover' , backgroundPosition : 'center'}
    const handleLocation = () => {
        if(location.pathname === '/landing'){
            return landingStyle
        }else{
            if(location.pathname === '/catalog'){
                return catalogStyle
            }else{
                return {}
            }
        }
    }
    const parentRef = useRef<HTMLDivElement>(null);

    const scrollToTop = () => {
        if (parentRef.current) {
            parentRef.current.scrollTo({ top: 0, behavior: 'smooth' });
        }
    };
  return (
    <Box sx={[{ display: "flex", flexDirection: "column", minHeight: "100vh", width: "100%" , height: "100vh",} , handleLocation() ]}>
        <Box sx={{ display: "flex", borderBottom: "1px solid #EAECF0", height: "64px", paddingRight: "2vh", paddingTop: "2.5px", paddingLeft: "2vh", position: "sticky", top: 0, backgroundColor: mode === 'Light' ? "white" : "#222", zIndex: 1100,}} onMouseLeave={closeCart} >
            <Box sx={{ display : 'flex', alignContent : 'center',  alignItems: 'center', }}>
                <Box component={"img"} src={"/img/part_master_logo.png"} sx={{ maxWidth : '190px' , marginRight : 5}}>
                </Box>
                <Tooltip title="Home">
                    <IconButton sx={sizing} color='primary' component={Link} to={'/landing'}>
                        <HomeIcon />
                    </IconButton>
                </Tooltip>

                <Tooltip title="Catalog">
                    <IconButton sx={sizing} color='primary' component={Link} to={'/catalog'}>
                        <ViewListIcon />
                    </IconButton>
                </Tooltip>
                {user.type === 'user' && 
                    <Tooltip title="My account">
                        <IconButton sx={sizing} color='primary' component={Link} to={'/account'}>
                            <AccountBalanceIcon />
                        </IconButton>
                    </Tooltip>
                }
                {user.type === 'admin' &&
                    <Tooltip title="Bot management">
                        <IconButton sx={sizing} color='primary' component={Link} to={'/bot'}>
                            <SmartToyIcon />
                        </IconButton>
                    </Tooltip>
                }
            </Box>
                <Box sx={{display : 'flex',  alignContent : 'center', marginLeft : 'auto'}}>
                    { location.pathname !== '/lists' && list.items.length > 0 && user.type === 'user' &&
                        <Box sx={{ position: 'relative' ,  marginTop : '4px' }} onMouseEnter={handleCart} >
                            <Tooltip title="Cart">
                                <IconButton sx={sizing} color='primary'component={Link} to={'/lists'}>
                                    <Badge badgeContent={list.items.length}>
                                        <ShoppingCartIcon />
                                    </Badge>
                                </IconButton>
                            </Tooltip>
                            <Collapse  sx={{ position: 'absolute', top: '100%', right: 0, bgcolor: 'background.paper', boxShadow: 3, zIndex: 10, width: 350, padding : 1 }} in={cartOpen} timeout={25}>
                                {loading ?                     
                                        <Box sx={{ display : 'flex' , justifyContent : 'center' , marginTop : 5 , height : 100}}>
                                            <CircularProgress size={50}/>
                                        </Box> :
                                    <Box sx={{  overflow : 'auto' , maxHeight : '800px' }}>
                                        { error !== '' &&
                                            <Box sx={{ width : '100%' , padding : 1 , border : '1px solid gray'}}><Typography sx={{ color : 'red'}}>{error}</Typography> <Typography sx={{ marginLeft : 'auto'}}>X</Typography></Box>
                                        }
                                        {list.items.map((item : i_parts , index : number) => (
                                            <Box key={index}>
                                                <CartRow key={item._id} part={item} callback={handleDelete} prepath={pathing} type={user.type} />
                                            </Box>
                                        ))}
                                        <Box sx={{ display : 'flex', marginTop : '10px' , justifyContent : 'space-evenly' , padding : 2}}>
                                            <Box component={Link} to={'/lists'}>View cart</Box>
                                            <Typography sx={{ alignSelf : 'center', color : 'black' , fontWeight : '800'}}>Total : {renderTotal(list.items , user)} $</Typography>
                                        </Box>
                                    </Box>
                                }
                            </Collapse>
                        </Box>
                    }
                    {user.type === 'admin' && location.pathname !== '/orders' &&
                         <Box sx={{ alignContent : 'center',  marginTop : '4px' }} onMouseEnter={handleCart} >
                            <Tooltip title="Cart">
                                <IconButton sx={sizing} color='primary'component={Link} to={'/lists'}>
                                    <Badge badgeContent={list.items.length}>
                                        <ShoppingBasketIcon />
                                    </Badge>
                                </IconButton>
                            </Tooltip>
                            <Collapse  sx={{ position: 'absolute', top: '100%', right: 0, bgcolor: 'background.paper', boxShadow: 3, zIndex: 10, width: 350, padding : 1 }} in={cartOpen} timeout={25}>
                                {loading ?                     
                                        <Box sx={{ display : 'flex' , justifyContent : 'center' , marginTop : 5 , height : 100}}>
                                            <CircularProgress size={50}/>
                                        </Box> :
                                    <Box sx={{  overflow : 'auto' , maxHeight : '800px' }}>
                                        { error !== '' &&
                                            <Box sx={{ width : '100%' , padding : 1 , border : '1px solid gray'}}><Typography sx={{ color : 'red'}}>{error}</Typography> <Typography sx={{ marginLeft : 'auto'}}>X</Typography></Box>
                                        }
                                        {list.items.map((item : i_parts , index : number) => (
                                            <Box key={index}>
                                                <CartRow key={item._id} part={item} callback={handleDelete} prepath={pathing} type={user.type} />
                                            </Box>
                                        ))}
                                        <Box sx={{ display : 'flex', marginTop : '10px' , justifyContent : 'space-evenly' , padding : 2}}>
                                            <Box component={Link} to={'/lists'}>View cart</Box>
                                            <Typography sx={{ alignSelf : 'center', color : 'black' , fontWeight : '800'}}>Total : {renderTotal(list.items , user)} $</Typography>
                                        </Box>
                                    </Box>
                                }
                            </Collapse>
                        </Box>
                    }
                    {user.type === 'admin' &&
                        <Box sx={{ marginTop : '4px',  alignContent : 'center',}}>
                            <Tooltip title="Message Center">
                                <IconButton sx={sizing} color='primary' component={Link} to={'/message-center'}>
                                    <Message />
                                </IconButton>
                            </Tooltip>
                            <Tooltip title={'Orders'}>
                                <IconButton sx={sizing} color='primary' component={Link} to={'/orders'}>
                                    <LibraryBooksIcon />
                                </IconButton>
                            </Tooltip>
                            <Tooltip title="Settings">
                                <IconButton sx={sizing} color='primary' onClick={handleSettingsToggle}>
                                    <SettingsIcon />
                                </IconButton>
                            </Tooltip>
                            <Menu anchorEl={settingsAnchorEl} open={settingsMenuOpen} onClose={handleClose} anchorOrigin={{ vertical: "bottom", horizontal: "left" }} transformOrigin={{ vertical: "top", horizontal: "left" }} >
                                <MenuItem component={Link} to={'/settings/users'} onClick={handleClose}>Users</MenuItem>
                                <MenuItem component={Link} to={'/settings/main-settings'} onClick={handleClose}>Settings</MenuItem>
                                <MenuItem component={Link} to={'/settings/preferences'} onClick={handleClose}>Preferences</MenuItem>
                                <MenuItem component={Link} to={'/settings/logs'} onClick={handleClose}>Logs</MenuItem>
                                <MenuItem component={Link} to={'/logout'} onClick={handleClose}>Logout</MenuItem>
                            </Menu>
                        </Box>
                    }
                    {user.type === 'user' &&
                        <Box sx={{ marginTop : '4px'}}>
                            <Tooltip title="logout">
                                <IconButton sx={sizing} component={Link} to={'/logout'} >
                                    <LogoutIcon/>
                                </IconButton>
                            </Tooltip>
                        </Box>
                    }
                </Box>
        </Box>
        <Box ref={parentRef} sx={{ flex : 1, overflow : 'auto' , height : '100%'}}>
            {list && cloneElement(props.component, { list, scrollToTop })} 
        </Box>
        <Footer/>
        </Box>
  );
};


export default TopBar;